/**
 * Exchange (UX-SPEC §4): tap tiles to select them, then "Exchange 3 tiles".
 * Disabled with a reason while the bag holds fewer than MIN_BAG_FOR_EXCHANGE.
 * Draft tiles are recalled first (the exchange ends the turn).
 */
import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { MIN_BAG_FOR_EXCHANGE } from '@wordloom/engine';
import type { GameView, Tile } from '@wordloom/engine';
import { sfx } from '../audio/sfx';
import { exchangeTiles } from '../game/actions';
import { useDraftStore } from '../state/draftStore';
import { useUiStore } from '../state/uiStore';
import { IconBag, IconExchange } from './Icons';
import { Modal } from './Modal';
import { tileLabel } from './Rack';
import { TileFace } from './Tile';
import { Tooltip } from './Tooltip';
import './ExchangeDialog.css';

export function ExchangeDialog({ game }: { game: GameView }) {
  const open = useUiStore((s) => s.dialog === 'exchange');
  const closeDialog = useUiStore((s) => s.closeDialog);
  const rackOrder = useDraftStore((s) => s.rackOrder);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const myTurn = game.status === 'playing' && game.currentSeat === game.you;

  // Fresh selection every time it opens.
  useEffect(() => {
    if (open) setSelected(new Set());
  }, [open]);

  // The turn ended underneath the dialog (timeout, game over): close it.
  useEffect(() => {
    if (open && !myTurn && !busy) closeDialog();
  }, [open, myTurn, busy, closeDialog]);

  // Every rack tile in the player's own order (tiles on the board included).
  const tiles = useMemo(() => {
    const byId = new Map(game.rack.map((t) => [t.id, t]));
    const ordered = rackOrder.map((id) => byId.get(id)).filter((t): t is Tile => t !== undefined);
    const missing = game.rack.filter((t) => !rackOrder.includes(t.id));
    return [...ordered, ...missing];
  }, [game.rack, rackOrder]);

  const count = selected.size;
  const bagShort = game.bagCount < MIN_BAG_FOR_EXCHANGE;
  const reason = bagShort
    ? `Exchanges need at least ${MIN_BAG_FOR_EXCHANGE} tiles in the bag (${game.bagCount} left).`
    : count === 0
      ? 'Select the tiles you want to swap.'
      : null;

  const toggle = (id: string) => {
    sfx.pick();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (busy) return;
    if (reason) {
      sfx.invalid();
      return;
    }
    setBusy(true);
    const ok = await exchangeTiles([...selected]);
    setBusy(false);
    if (ok) closeDialog();
  };

  const label = count === 0 ? 'Exchange' : `Exchange ${count} ${count === 1 ? 'tile' : 'tiles'}`;

  return (
    <Modal
      open={open}
      onClose={closeDialog}
      title="Exchange tiles"
      description="Choose tiles to put back in the bag. You draw the same number of new tiles, and your turn ends."
      size="md"
      footer={
        <>
          <button type="button" className="btn" onClick={closeDialog}>
            Cancel
          </button>
          <Tooltip content={reason ?? `Swap ${count} and end your turn`}>
            <button
              type="button"
              className="btn btn--primary"
              aria-disabled={Boolean(reason) || busy || undefined}
              onClick={() => void submit()}
            >
              {busy ? <span className="btn__spinner" aria-hidden="true" /> : <IconExchange />}
              {label}
            </button>
          </Tooltip>
        </>
      }
    >
      <div className="exchange">
        <div className="exchange__tiles" role="group" aria-label="Your tiles">
          {tiles.map((t) => {
            const on = selected.has(t.id);
            return (
              <button
                key={t.id}
                type="button"
                className={clsx('exchange__slot', on && 'is-selected')}
                aria-pressed={on}
                aria-label={tileLabel(t)}
                disabled={bagShort}
                onClick={() => toggle(t.id)}
              >
                <span className="tile exchange__tile" data-blank={t.blank || undefined}>
                  <TileFace letter={t.blank ? '' : t.letter} value={t.value} blank={t.blank} />
                </span>
              </button>
            );
          })}
        </div>
        <div className="exchange__meta">
          <span className="exchange__bag">
            <IconBag /> {game.bagCount} {game.bagCount === 1 ? 'tile' : 'tiles'} in the bag
          </span>
          {!bagShort && (
            <span className="exchange__tools">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setSelected(new Set(tiles.map((t) => t.id)))}
              >
                Select all
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                aria-disabled={count === 0 || undefined}
                onClick={() => setSelected(new Set())}
              >
                Clear
              </button>
            </span>
          )}
        </div>
        {bagShort && (
          <p className="exchange__note" role="note">
            {reason}
          </p>
        )}
      </div>
    </Modal>
  );
}
